import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Tooltip } from 'react-tooltip';
import {ReactComponent as IconExchange} from '../../../assets/icons/ic-exchange.svg'
import {ReactComponent as IconTooltip } from '../../../assets/icons/ic-tooltip.svg'
import Toast from '../../../plugins/useToast';

import { useRecoilState } from 'recoil';
import axios from '../../../plugins/axios';
import { wardState } from '../../../plugins/recoil';

function DragDrop(props) {
    const { t } = useTranslation()
    const {value1, setValue1, value2, setValue2, connectedBeds, setConnectedBeds, customBedConfig1, customBedConfig2, mainMonitorBedCount, onUnassignComplete, monitorSetting, leftBedCount, rightBedCount, setLeftBedCount, setRightBedCount} = props;
    const [curWard] = useRecoilState(wardState);

    const handleOnDragEnd = (result) => {
        const { source, destination } = result;
        // console.log("Drag Event:", result); 
        if (!destination || destination.index === null) {
            console.warn("No destination detected");
            return;
        }
        if (!destination || destination.index === null) return;

         // 화면 구성 예시에서 연결된 배드로 드래그 방지
        if (
            (source.droppableId === 'value1' || source.droppableId === 'value2') &&
            destination.droppableId === 'connectedBeds'
        ) {
            return;
        }
    
        const reorderList = (list, setList) => {
            const newList = [...list];
            const [movedItem] = newList.splice(source.index, 1);
            newList.splice(destination.index, 0, movedItem);
            setList(newList);
        };

        // maxLength: 고정 모드일 경우, 각 리스트의 슬롯 수는 mainMonitorBedCount의 절반.
        const getMaxLength = () => {
            // customBedConfig1 === false인 경우(고정 모드)에는 mainMonitorBedCount에서 숫자 추출
            return customBedConfig1
            ? 8
            : parseInt(mainMonitorBedCount.split(" ")[0], 10) / 2;
        };
    
        const moveBetweenLists = (fromList, toList, setFromList, setToList) => {
            const newFromList = [...fromList];
            const newToList = [...toList];
            const draggedItem = newFromList[source.index];
            if (!draggedItem || !draggedItem.deviceId) {
                console.warn("Cannot drag empty or undefined item.");
                return;
            }

            if (customBedConfig1) {
                let insertIndex = destination.index;
                if (newToList[insertIndex] && newToList[insertIndex].deviceId) {
                    insertIndex = newToList.findIndex((bed) => !bed.deviceId);
                    if (insertIndex === -1) {
                        console.warn("No empty slot available in the destination list.");
                        return;
                    }
                }
                newToList[insertIndex] = { ...draggedItem };
                newFromList[source.index] = { bedNumber: '', deviceId: '' };
                setFromList(newFromList);
                setToList(newToList);
            } else {
                const maxListLength = mainMonitorBedCount.split(" ")[0] / 2;
                const currentListLength = newToList.filter((bed) => bed.deviceId).length;
                if (currentListLength >= maxListLength) {
                    console.warn("Cannot add more items. List length exceeded.");
                    return;
                }
                if (destination.index >= newToList.length) {
                    console.warn("Destination index is beyond fixed slot length.");
                    return;
                }
                while (newToList.length < destination.index + 1) {
                    newToList.push({ deviceId: '' });
                }
                const targetSlot = newToList[destination.index];
                if (!targetSlot.deviceId) {
                    newToList[destination.index] = { ...draggedItem };
                } else {
                    newToList.splice(destination.index, 0, { ...draggedItem });
                }
                newFromList[source.index] = { bedNumber: '', deviceId: '' };
                while (newToList.length > maxListLength) {
                    const emptyIndex = newToList
                        .slice()
                        .reverse()
                        .findIndex((bed) => !bed.deviceId);
                    if (emptyIndex !== -1) {
                        const actualIndex = newToList.length - 1 - emptyIndex;
                        newToList.splice(actualIndex, 1);
                    } else {
                        break;
                    }
                }
                setFromList(newFromList);
                setToList(newToList);
            }
        };
    
        const moveFromConnectedBeds = (toList, setToList) => {
            let draggedDeviceId = result.draggableId;
            if (draggedDeviceId.startsWith('connected-')) {
                draggedDeviceId = draggedDeviceId.replace('connected-', '').replace(/-\d+$/, '');
            }
            const draggedItem = connectedBeds.find((bed) => String(bed.deviceId) === String(draggedDeviceId));
            if (!draggedItem) {
                Toast({ isSuccess: false , msg : t('error.dragFailed') });
                return;
            }
            const maxLength = getMaxLength();
            const newToList = [...toList];
            const currentFilled = newToList.filter((bed) => bed.deviceId).length;
            if (currentFilled >= maxLength) {
                Toast({ isSuccess: false , msg : t('error.listFull') });
                return;
            }
            if (!customBedConfig1 && destination.index >= newToList.length) {
                Toast({ isSuccess: false , msg : t('error.layoutFull') });
                return;
            }
            let insertIndex = destination.index;
            if (newToList[insertIndex] && newToList[insertIndex].deviceId) {
                const emptyIndex = newToList.findIndex((slot) => !slot.deviceId);
                if (emptyIndex === -1) {
                    Toast({ isSuccess: false , msg : t('error.full') });
                    return;
                }
                insertIndex = emptyIndex;
            }
            newToList[insertIndex] = { ...draggedItem, isRemoved: false };
            const newConnectedBeds = connectedBeds.filter((bed) => bed.deviceId !== draggedItem.deviceId);
            setConnectedBeds(newConnectedBeds);
            setToList(newToList);
        };   
    
        const listMap = {
            value1: { list: value1, setList: setValue1 },
            value2: { list: value2, setList: setValue2 },
            // 필요에 따라 다른 리스트들도 추가
            connectedBeds: { list: connectedBeds, setList: setConnectedBeds },
        };
    
        if (source.droppableId === destination.droppableId) {
            const { list, setList } = listMap[source.droppableId];
            reorderList(list, setList);
        } else {
            const fromListObj = listMap[source.droppableId];
            const toListObj = listMap[destination.droppableId];
    
            if (fromListObj && toListObj) {
                if (source.droppableId === 'connectedBeds') {
                    moveFromConnectedBeds(toListObj.list, toListObj.setList);
                } else {
                    moveBetweenLists(fromListObj.list, toListObj.list, fromListObj.setList, toListObj.setList);
                }
            }
        }
    };    
    
    const handleDecrease = (setValue, value, column) => {
        if (value > 2) {
            let removedItem;
            setValue((prev) => {
                removedItem = prev[prev.length - 1];
                return prev.slice(0, -1);
            });
            // custom 모드에서 칸수도 함께 줄이기
            if (customBedConfig1) {
                if (column === 'left' && typeof setLeftBedCount === 'function') {
                    setLeftBedCount((prev) => Math.max(2, prev - 1));
                }
                if (column === 'right' && typeof setRightBedCount === 'function') {
                    setRightBedCount((prev) => Math.max(2, prev - 1));
                }
            }
            if (props.onBedCountChange) {
                props.onBedCountChange(column, -1);
            }
            if (removedItem && removedItem.deviceId) {
                setConnectedBeds((prevConnected) => [
                    ...prevConnected.filter(bed => bed.deviceId !== removedItem.deviceId),
                    { ...removedItem, isRemoved: true, monitorNumbers: [] }
                ]);
            }
        }
    };
    
    const handleIncrease = (setValue, value, column) => {
        if (value < 8) {
            setValue((prev) => [
                ...prev,
                { bedNumber: '', deviceId: '' }
            ]);
            // custom 모드에서 칸수도 함께 늘리기
            if (customBedConfig1) {
                if (column === 'left' && typeof setLeftBedCount === 'function') {
                    setLeftBedCount((prev) => Math.min(8, prev + 1));
                }
                if (column === 'right' && typeof setRightBedCount === 'function') {
                    setRightBedCount((prev) => Math.min(8, prev + 1));
                }
            }
        }
    };

    // 항목 삭제 후 연결된 배드로 이동
    const handleRemoveItem = (item, sourceId) => {
        if (sourceId === 'value1') {
            setValue1((prev) =>
                prev.map((i) => (i.deviceId === item.deviceId ? { ...i, bedNumber: '', deviceId: '' } : i))
            );
        } else if (sourceId === 'value2') {
            setValue2((prev) =>
                prev.map((i) => (i.deviceId === item.deviceId ? { ...i, bedNumber: '', deviceId: '' } : i))
            );
        }
    };

    // 체크박스 선택 상태 관리
    const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

    function handleCheckboxToggle(item) {
        setSelectedDeviceIds(prev => {
            if (prev.includes(item.deviceId)) {
                return prev.filter(id => id !== item.deviceId);
            } else {
                return [...prev, item.deviceId];
            }
        });
    }

function handleUnassignAll() {
    if (!selectedDeviceIds || selectedDeviceIds.length === 0) return;
    const deletePromises = [];
    selectedDeviceIds.forEach(deviceId => {
        const bed = connectedBeds.find(b => String(b.deviceId) === String(deviceId));
        const monitorNumbers = bed && Array.isArray(bed.monitorNumbers) ? bed.monitorNumbers : [];
        let allMonitorNumbers = [...monitorNumbers];
        if (allMonitorNumbers.length === 0) {
            [...value1, ...value2].forEach(b => {
                if (String(b.deviceId) === String(deviceId) && Array.isArray(b.monitorNumbers)) {
                    allMonitorNumbers = b.monitorNumbers;
                }
            });
        }

        if (!allMonitorNumbers || allMonitorNumbers.length === 0) {
            allMonitorNumbers = [1];
        }
        allMonitorNumbers.forEach(monitorNo => {
            deletePromises.push(
                axios.delete(`/ward/${curWard.value}/dashboard/setting/${monitorNo}/device/${deviceId}`)
                    .catch(error => ({ error, deviceId, monitorNo }))
            );
        });
    });

    Promise.all(deletePromises).then(results => {
        const failed = results.filter(r => r && r.error);
        if (failed.length > 0) {
            Toast({ isSuccess: false, msg: t('error.unassignFailed', { failedDevices: failed.map(f => `${f.deviceId}(Dash${f.monitorNo})`).join(', ') })});
        } else {
            Toast({ isSuccess: true, msg: t('setting.unassignSuccess') });
        }

        setValue1(prev => prev.map(bed => selectedDeviceIds.includes(bed.deviceId) ? { ...bed, bedNumber: '', deviceId: '' } : bed));
        setValue2(prev => prev.map(bed => selectedDeviceIds.includes(bed.deviceId) ? { ...bed, bedNumber: '', deviceId: '' } : bed));
        setConnectedBeds(prev => {
            const already = prev.map(bed => bed.deviceId);
            const addBeds = [...value1, ...value2]
                .filter(bed => selectedDeviceIds.includes(bed.deviceId) && bed.deviceId && !already.includes(bed.deviceId))
                .map(bed => ({ ...bed, isRemoved: true, monitorNumbers: [] }));
            const updated = prev.map(bed => selectedDeviceIds.includes(bed.deviceId) ? { ...bed, isRemoved: true, monitorNumbers: [] } : bed);
            return [...updated, ...addBeds];
        });
        setSelectedDeviceIds([]);
        if (props.onUnassignComplete) {
            props.onUnassignComplete();
        }
    });
} 

    return (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <div className={`${customBedConfig2 ? 'w-[886px]' : 'w-[563px]'} bg-[#2F2F2F] p-4 rounded-md`}>
                <h3 className="text-[18px] mb-4">{t('setting.screenLayout')}​</h3>
                <div className='flex h-[298px]'>
                    <div className='w-[523px]'>
                        <div className="flex text-sm text-center overflow-hidden bg-[#4E4E4E]">
                            <Droppable droppableId="value1">
                                {(provided) => {
                                    // 옵션에서 Bed 개수 계산
                                    const totalBeds = parseInt((mainMonitorBedCount || '16 Bed').split(' ')[0], 10) || 16;
                                    // const leftBedCount = monitorSetting?.leftBedCount || Math.ceil(totalBeds / 2);
                                    const paddedValue1 = [...value1];
                                    while (paddedValue1.length < leftBedCount) paddedValue1.push({ bedNumber: '', deviceId: '' });
                                    return (
                                    <div className="w-[260px] h-[265px]" ref={provided.innerRef} {...provided.droppableProps}>
                                    {paddedValue1.map((item, i) => (
                                        <Draggable
                                            key={`value1-${item.deviceId}-${i}`}
                                            draggableId={`value1-${item.deviceId}-${i}`}
                                            index={i}
                                            isDragDisabled={!item.deviceId} // 항목이 삭제된 경우에만 드래그 비활성화
                                        >
                                        {(provided, snapshot) => (
                                            <div
                                            className={`relative flex items-center gap-5 bg-black border px-3 group ${snapshot.isDragging? 'border-[#3182F7]' : 'border-[#4E4E4E]'}`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                height: `${265 / paddedValue1.length}px`,
                                                backgroundColor: snapshot.isDragging
                                                    ? 'rgba(0, 0, 0, 0.7) ' // 드래그 중
                                                    : 'black',
                                            }}
                                            >
                                            {/* 아이템이 있을 때만 보여주기 */}
                                            {item.deviceId ? (
                                                <>
                                                {/* <span className='w-[100px] text-[16px] text-left font-medium'>
                                                    {`BED-${item.bedNumber.length <= 1 ? `0${item.bedNumber}` : item.bedNumber}`}
                                                </span> */}
                                                <span className='w-[100px] text-[16px] text-left font-medium'>{item.bedNumber != null ? `BED-${String(item.bedNumber).padStart(2, '0')}` : ''}</span>
                                                <div className="flex flex-col items-start gap-1">
                                                    
                                                    <span className='!font-light text-[16px] text-left w-[115px] overflow-hidden text-ellipsis whitespace-nowrap'title={item.patientName}>
                                                    {
                                                        value1.length <= 6 ? (
                                                            !item.patientId ? 'Not Admitted' : 
                                                            (item.devicePatientId && item.patientName
                                                            ? (
                                                                <>
                                                                    {item.patientName}
                                                                    <br />
                                                                    {item.devicePatientId}
                                                                </>
                                                                )
                                                            : item.patientName || item.devicePatientId || '-')
                                                        ) : (
                                                            !item.patientId ? 'Not Admitted' : (item.devicePatientId && item.patientName ? `${item.patientName} , ${item.devicePatientId}`: item.patientName || item.devicePatientId || '-')
                                                        )
                                                    }
                                                    </span>
                                                </div>
                                                <button className="absolute right-0 top-1/2 transform -translate-y-1/2 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                    onClick={() => handleRemoveItem(item, 'value1')}>
                                                    X
                                                </button>
                                                </>
                                            ) :  (
                                                <span className=""></span>
                                            )}
                                            </div>
                                        )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    </div>
                                )}}
                            </Droppable>
                        
                            <Droppable droppableId="value2">
                                {(provided) => {
                                    const totalBeds = parseInt((mainMonitorBedCount || '16 Bed').split(' ')[0], 10) || 16;
                                    // const rightBedCount = monitorSetting?.rightBedCount || Math.floor(totalBeds / 2);
                                    const paddedValue2 = [...value2];
                                    while (paddedValue2.length < rightBedCount) paddedValue2.push({ bedNumber: '', deviceId: '' });
                                    return (
                                    <div className="w-1/2 h-[265px]" ref={provided.innerRef} {...provided.droppableProps}>
                                    {paddedValue2.map((item, i) => (
                                        <Draggable
                                        key={`value2-${item.deviceId ? String(item.deviceId) : `empty-${i}`}`}
                                        draggableId={item.deviceId ? String(item.deviceId) : `empty-${i}`}
                                        index={i}
                                        isDragDisabled={!item.deviceId} // 항목이 삭제된 경우에만 드래그 비활성화
                                        >
                                        {(provided, snapshot) => (
                                            <div
                                            className={`relative flex items-center gap-5 bg-black border px-3 group ${snapshot.isDragging? 'border-[#3182F7]' : 'border-[#4E4E4E]'}`}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                    height: `${265 / paddedValue2.length}px`,
                                                    backgroundColor: snapshot.isDragging
                                                    ? 'rgba(0, 0, 0, 0.7) ' // 드래그 중
                                                    : 'black',
                                                }}
                                            >
                                            {/* 아이템이 있을 때만 보여주기 */}
                                            {item.deviceId ? (
                                                <>
                                                {/* <span className='w-[100px] text-[16px] text-left font-medium'>{`BED-${item.bedNumber.length <= 1 ? `0${item.bedNumber}` : item.bedNumber}`}</span> */}
                                                <span className='w-[100px] text-[16px] text-left font-medium'>{item.bedNumber != null ? `BED-${String(item.bedNumber).padStart(2, '0')}` : ''}</span>
                                                <div className="flex flex-col items-start gap-1">                                                    
                                                    <span className='!font-light text-[16px] text-left w-[115px] overflow-hidden text-ellipsis whitespace-nowrap'title={item.patientName}>                                                        
                                                        {
                                                            value2.length <= 6 ? (
                                                                !item.patientId ? 'Not Admitted' : 
                                                                (item.devicePatientId && item.patientName
                                                                ? (
                                                                    <>
                                                                        {item.patientName}
                                                                        <br />
                                                                        {item.devicePatientId}
                                                                    </>
                                                                    )
                                                                : item.patientName || item.devicePatientId || '-')
                                                            ) : (
                                                                !item.patientId ? 'Not Admitted' : (item.devicePatientId && item.patientName ? `${item.patientName} , ${item.devicePatientId}`: item.patientName || item.devicePatientId || '-')
                                                            )
                                                        }
                                                    </span>
                                                </div>
                                                <button
                                                    className="absolute right-0 top-1/2 transform -translate-y-1/2 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                    onClick={() => handleRemoveItem(item, 'value2')}
                                                >
                                                    X
                                                </button>
                                                </>
                                            ) :  (
                                                <span className=""></span>
                                            )}
                                            </div>
                                        )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}}
                            </Droppable>
                        </div>
                        {customBedConfig1 && (
                            <div className="mt-2 mx-auto gap-[127px] flex justify-center items-center font-light">
                                <div className="flex items-center justify-between rounded-lg border border-[#4E4E4E] bg-[#1F1F1F] overflow-hidden w-[134px] h-[26px]">
                                    <button 
                                        onClick={() => handleDecrease(setValue1, value1.length, 'left')}
                                        disabled={value1.length <= 2}
                                        className={`w-[26px] h-full flex items-center justify-center text-[16px] ${
                                            value1.length <= 2 ? 'bg-[#3A3A3A] text-[#8E8E8E] cursor-default' : 'bg-[#5E5E5E] text-white'
                                        }`}>
                                        -
                                    </button>
                                    <span className="w-[24px] text-center text-white">{value1.length}</span>
                                    <button
                                        onClick={() => handleIncrease(setValue1, value1.length, 'left')}
                                        disabled={value1.length >= 8}
                                        className={`w-[26px] h-full flex items-center justify-center text-[16px] ${
                                            value1.length >= 8 ? 'bg-[#3A3A3A] text-[#8E8E8E] cursor-default' : 'bg-[#5E5E5E] text-white'
                                        }`}>
                                        +
                                    </button>
                                </div>
                        
                                <div className="flex items-center justify-between rounded-lg border border-[#4E4E4E] bg-[#1F1F1F] overflow-hidden w-[134px] h-[26px]">
                                    <button
                                        onClick={() => handleDecrease(setValue2, value2.length, 'right')}
                                        disabled={value2.length <= 2}
                                        className={`w-[26px] h-full flex items-center justify-center text-[16px] ${
                                            value2.length <= 2 ? 'bg-[#3A3A3A] text-[#8E8E8E] cursor-default' : 'bg-[#5E5E5E] text-white'
                                        }`}>
                                        -
                                    </button>
                                    <span className="w-[24px] text-center text-white">{value2.length}</span>
                                    <button
                                        onClick={() => handleIncrease(setValue2, value2.length, 'right')}
                                        disabled={value2.length >= 8}
                                        className={`w-[26px] h-full flex items-center justify-center text-[16px] ${
                                            value2.length >= 8 ? 'bg-[#3A3A3A] text-[#8E8E8E] cursor-default' : 'bg-[#5E5E5E] text-white'
                                        }`}>
                                        +
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
            <div className="w-[560px] bg-[#2F2F2F] p-4 rounded-md">
                <div className='mb-4 flex items-center gap-1'>
                    <h3 className="text-[18px]">{t('setting.connectedBed')}</h3>
                    <div data-tooltip-id="tooltip" style={{cursor: 'pointer'}}>
                        <IconTooltip className="size-[18px]" />
                    </div>
                    <Tooltip id="tooltip" place="bottom" effect="solid" opacity={1} style={{backgroundColor: 'white', borderRadius: '8px', width: '310px', height: '105px', zIndex: '1000'}}>
                        <div className=''>
                            <p className="text-[#000] opacity-60 text-[14px] mb-2">Status</p>
                            <div className="text-black !font-light text-[16px] flex gap-2 mb-2">
                                <div className='bg-[#1f1f1f] w-[28px] h-[24px] block border border-[#4E4E4E]'></div>
                                {t('setting.displayedBed')}</div>
                            <div className="text-black leading-6 !font-light text-[16px] flex gap-2">
                                <div className='bg-[#DEDEDE] w-[28px] h-[24px] block'></div>
                                {t('setting.displayableBed')}</div>
                        </div>
                    </Tooltip>
                    <button
                        onClick={handleUnassignAll}
                        disabled={!selectedDeviceIds || selectedDeviceIds.length === 0}
                        className={`w-[100px] h-[28px] ml-[273px] rounded-[6px] text-white bg-gradient-to-b from-[#5E5E5E] to-[#383838] shadow-md text-[16px] font-light transition-opacity duration-200 ${(!selectedDeviceIds || selectedDeviceIds.length === 0) ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                        {t('setting.unassign')}
                    </button>
                </div>
                <Droppable droppableId="connectedBeds">
                    {(provided) => {
                        // 화면 구성 예시에 포함된 배드들을 상단으로 정렬
                        const sortedConnectedBeds = [...connectedBeds].sort((a, b) => {
                            const isInExampleA =
                                value1.some((bed) => bed.deviceId === a.deviceId) ||
                                value2.some((bed) => bed.deviceId === a.deviceId);

                            const isInExampleB =
                                value1.some((bed) => bed.deviceId === b.deviceId) ||
                                value2.some((bed) => bed.deviceId === b.deviceId);

                            // 1. 화면 구성 예시에 포함된 배드는 상단으로
                            if (isInExampleA && !isInExampleB) return -1;
                            if (!isInExampleA && isInExampleB) return 1;

                            // 2. 나머지 배드들은 배드 넘버 기준으로 정렬
                            const bedNumberA = parseInt(a.bedNumber, 10);
                            const bedNumberB = parseInt(b.bedNumber, 10);

                            return bedNumberA - bedNumberB;
                        });

                        return (
                            <div className="relative">
                                <div className="scrollbar" ref={provided.innerRef} {...provided.droppableProps} style={{ overflowY: "auto", height: 270 }}>
                                    <div className="sticky top-0 z-10 flex items-center h-[42px] bg-[#232323] border border-[#4E4E4E] border-b-0" style={{minWidth:'100%', fontWeight:300}}>
                                        <span className="w-[132px] pl-[38px] text-[14px] text-[#DEDEDE]">{t('setting.bedNo')}</span>
                                        <span className="w-[235px] text-[14px] text-[#DEDEDE]">{t('setting.patient')}</span>
                                        <span className="flex-1 text-[14px] text-[#DEDEDE ]">{t('setting.dashboard')}</span>
                                    </div>
                                    {(Array.isArray(sortedConnectedBeds) ? sortedConnectedBeds : []).map((item, i) => {
                                        // value1, value2에 이미 포함된 항목은 드래그 비활성화
                                        const isInValue1Or2 = value1.some(bed => bed.deviceId === item.deviceId) || value2.some(bed => bed.deviceId === item.deviceId);
                                        // isRemoved:true면 드래그 가능, value1/value2에 이미 있으면 불가
                                        const isDraggable = item.isRemoved === true && !isInValue1Or2;
                                        if (!item.deviceId) return null;
                                        return (
                                            <Draggable
                                                key={`connected-${item.deviceId || item.patientId || 'empty'}-${i}`}
                                                draggableId={`connected-${item.deviceId}`}
                                                index={i}
                                                isDragDisabled={!isDraggable}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        className={`flex items-center h-[32px] gap-2 border px-3
                                                            ${snapshot.isDragging
                                                            ? 'border-[#3182F7]'
                                                            : selectedDeviceIds.includes(item.deviceId)
                                                                ? 'border-[#3182F7]'
                                                                : 'border-[#4E4E4E]'
                                                        }
                                                        ${!isDraggable ? '' : ''}
                                                    `}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        backgroundColor: snapshot.isDragging
                                                            ? 'rgba(0, 0, 0, 0.7)'
                                                            : item.isRemoved
                                                            ? '#DEDEDE'
                                                            : 'black',
                                                        color: snapshot.isDragging
                                                            ? 'white'
                                                            : item.isRemoved
                                                            ? 'black'
                                                            : '#dedede',
                                                    }}
                                                >
                                                    {item.monitorNumbers && item.monitorNumbers.length > 0 ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDeviceIds.includes(item.deviceId)}
                                                            onChange={() => handleCheckboxToggle(item)}
                                                            className={item.isRemoved ? '!bg-[#cfcfcf] !border-[#C2C2C2]' : ''}
                                                        />
                                                    ) : (
                                                        <span className="w-[20px]"></span>
                                                    )}
                                                    {/* <span className='w-[80px] text-[16px] font-medium truncate'>
                                                        {`BED-${item.bedNumber && item.bedNumber.length <= 1 ? `0${item.bedNumber}` : item.bedNumber}`}
                                                    </span> */}
                                                    <span className='w-[80px] text-[16px] font-medium truncate'>
                                                        {item.bedNumber != null ? `BED-${String(item.bedNumber).padStart(2, '0')}` : ''}
                                                    </span>
                                                    <div className="flex items-center gap-1 w-[235px]">
                                                        <span className='!font-light text-[16px] truncate' title={(item.patientName ? item.patientName : '--') + " , " + (item.devicePatientId ? item.devicePatientId : '--')}>
                                                        {/* {item.patientName
                                                            ? `${item.patientName}, ${item.devicePatientId && item.devicePatientId !== 'null' ? item.devicePatientId : '-'}`
                                                            : item.devicePatientId && item.devicePatientId !== 'null'
                                                                ? item.devicePatientId
                                                                : '-'} */}
                                                        {/* {item.devicePatientId && item.patientName ? `${item.devicePatientId}, ${item.patientName}`: item.devicePatientId || item.patientName || '-'} */}
                                                        {!item.patientId ? 'Not Admitted' : (item.devicePatientId && item.patientName ? `${item.patientName} , ${item.devicePatientId}`: item.patientName || item.devicePatientId || '-')}
                                                        </span>
                                                    </div>
                                                    <span className=" text-[16px]">
                                                        {item.monitorNumbers && item.monitorNumbers.length > 0 ? (
                                                            item.monitorNumbers.map((n, idx) => (
                                                                <span key={n} className="">
                                                                    {idx > 0 ? ', ' : ''}{t('setting.dashboardShort')}{n}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-[16px]">-</span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                </div>
                                {provided.placeholder}
                            </div>
                        );
                    }}
                </Droppable>

            </div>
        </DragDropContext>
    )
}

export default DragDrop